import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  JEV_BUDGET_SET_PRESETS,
  JEV_BUDGET_WARN_AT_PERCENT,
  jevBudgetAmountProblem,
  jevBudgetLabel,
  jevBudgetPresetLabel,
  jevBudgetRaiseOptions,
  jevBudgetStandingOf,
  type JevBudgetHistoryLine,
  type JevBudgetView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { JevBudgetPicker } from '../../../components/JevBudgetPicker';
import { SkeletonLines } from '../../../components/Skeleton';
import { errorMessage } from '../../../lib/api-client';
import { formatDate } from '../../../lib/format';
import { useAskJevBudget, useJevBudget, useRaiseJevBudget, useSetJevBudget } from '../api/jev-budget-api';
import styles from './JevBudgetPanel.module.css';

// ADR 0070, stage 1 — the job's Jev budget on the Ranking page: what is left of what was approved, how much Jev has cost, who may raise it,
// and the history. Stage 2: when it is used up Jev stops reading for the job, and the panel says so.

const NO_BUDGET = 'No Jev budget set';
const RAISE_ONLY_HR = 'Only HR can raise this budget.';
const NEARLY_USED = 'Reading stops when it runs out.';
const USED_UP = 'Reading has stopped for this job.';
const ASKED = 'Asked. HR has been told';

/** The `#jev-budget` anchor the Jev budget notifications land on (N-95, N-96, N-97). */
export const JEV_BUDGET_ANCHOR = 'jev-budget';

interface JevBudgetPanelProps {
  readonly jobId: string;
  /** What Jev has cost on this job so far: the figure the Ideal candidate card shows (never a second source). Without it, the server's, which is the same figure. */
  readonly spentMicroUsd?: number;
  /** The page was opened at `#jev-budget` (a notification): scroll the panel into view and pick it out. */
  readonly isLinkedTo?: boolean;
}

export function JevBudgetPanel({ jobId, spentMicroUsd: shownSpent, isLinkedTo = false }: JevBudgetPanelProps): ReactNode {
  const query = useJevBudget(jobId);
  const view = query.data;
  const anchor = useRef<HTMLDivElement | null>(null);
  const isReady = view !== undefined;
  useEffect(() => {
    // Wait for the figures, so the page has its final height before it scrolls here.
    if (isLinkedTo && isReady && typeof anchor.current?.scrollIntoView === 'function') anchor.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [isLinkedTo, isReady]);
  return (
    <div id={JEV_BUDGET_ANCHOR} ref={anchor} className={styles.anchor} data-linked={isLinkedTo || undefined}>
      <PanelCard jobId={jobId} query={query} shownSpent={shownSpent} />
    </div>
  );
}

function PanelCard({ jobId, query, shownSpent }: { readonly jobId: string; readonly query: ReturnType<typeof useJevBudget>; readonly shownSpent: number | undefined }): ReactNode {
  const view = query.data;
  if (!view) {
    return (
      <Card title="Jev budget">
        {query.isError ? (
          <div className={styles.problem}>
            <span>{errorMessage(query.error)}</span>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <SkeletonLines count={2} />
        )}
      </Card>
    );
  }
  const spentMicroUsd = shownSpent ?? view.spentMicroUsd;
  return (
    <Card title="Jev budget">
      {view.budgetMicroUsd === null ? <NoBudget jobId={jobId} view={view} spentMicroUsd={spentMicroUsd} /> : <Budget jobId={jobId} view={view} budgetMicroUsd={view.budgetMicroUsd} spentMicroUsd={spentMicroUsd} />}
      <History lines={view.history} />
    </Card>
  );
}

function Budget({ jobId, view, budgetMicroUsd, spentMicroUsd }: { readonly jobId: string; readonly view: JevBudgetView; readonly budgetMicroUsd: number; readonly spentMicroUsd: number }): ReactNode {
  const raise = useRaiseJevBudget(jobId);
  const ask = useAskJevBudget(jobId);
  const { leftMicroUsd, percentUsed, tone } = jevBudgetStandingOf(budgetMicroUsd, spentMicroUsd);
  const word = view.history.some((line) => line.kind === 'approved') ? 'approved' : 'set';
  const options = jevBudgetRaiseOptions(budgetMicroUsd, spentMicroUsd);
  return (
    <div className={styles.body}>
      <p className={styles.figures}>
        <span className={styles.left}>{jevBudgetLabel(leftMicroUsd)} left</span>
        <span className={styles.of}>
          of {jevBudgetLabel(budgetMicroUsd)} {word} · {jevBudgetLabel(spentMicroUsd)} spent
        </span>
      </p>
      <div className={styles.bar} data-tone={tone} role="progressbar" aria-label="Jev budget used" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percentUsed}>
        <span className={styles.fill} style={{ inlineSize: `${percentUsed}%` }} />
      </div>
      {percentUsed >= JEV_BUDGET_WARN_AT_PERCENT && (
        <p className={styles.notice} data-tone={tone} role="status">
          <strong>{percentUsed >= 100 ? 'Used up.' : `${percentUsed}% used.`}</strong> {percentUsed >= 100 ? USED_UP : NEARLY_USED}
        </p>
      )}
      {view.canRaise ? (
        <>
          {view.openAsk && (
            <p className={styles.notice} data-tone="warning" role="status">
              <strong>{view.openAsk.byName ?? 'A recruiter'} asked for more</strong> on {formatDate(view.openAsk.at)}. Raise the budget below.
            </p>
          )}
          {options.length > 0 && (
            <div className={styles.actions}>
              <span className={styles.label}>Raise to</span>
              {options.map((amount) => (
                <Button key={amount} variant="outline" size="sm" isPending={raise.isPending && raise.variables?.budgetMicroUsd === amount} onClick={() => raise.mutate({ budgetMicroUsd: amount })}>
                  {jevBudgetPresetLabel(amount)}
                </Button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <p className={styles.note}>{RAISE_ONLY_HR}</p>
          {view.canAsk &&
            (view.openAsk ? (
              <div className={styles.actions}>
                <Button variant="solid" size="sm" disabled>
                  {ASKED}
                </Button>
              </div>
            ) : (
              <div className={styles.actions}>
                <Button variant="solid" size="sm" isPending={ask.isPending} onClick={() => ask.mutate()}>
                  Ask HR for more
                </Button>
              </div>
            ))}
        </>
      )}
    </div>
  );
}

function NoBudget({ jobId, view, spentMicroUsd }: { readonly jobId: string; readonly view: JevBudgetView; readonly spentMicroUsd: number }): ReactNode {
  const set = useSetJevBudget(jobId);
  const [amount, setAmount] = useState<number | null>(null);
  return (
    <div className={styles.body}>
      <p className={styles.figures}>
        <span className={styles.left}>{NO_BUDGET}</span>
        <span className={styles.of}>No limit · {jevBudgetLabel(spentMicroUsd)} spent</span>
      </p>
      {view.canSet ? (
        <div className={styles.setter}>
          <JevBudgetPicker label="Jev budget for this job" value={amount} presets={JEV_BUDGET_SET_PRESETS} onChange={setAmount} emptyHint="Pick an amount to set." />
          <Button variant="solid" size="sm" disabled={amount === null || jevBudgetAmountProblem(amount) !== null} isPending={set.isPending} onClick={() => amount !== null && jevBudgetAmountProblem(amount) === null && set.mutate({ budgetMicroUsd: amount })}>
            Set Jev budget
          </Button>
        </div>
      ) : (
        <p className={styles.note}>Only the job’s recruiter or HR can set one.</p>
      )}
    </div>
  );
}

const HISTORY_WORDS: Readonly<Record<JevBudgetHistoryLine['kind'], string>> = {
  requested: 'asked for',
  approved: 'approved',
  set: 'set it to',
  raised: 'raised it to',
  asked: 'asked for more',
};

function History({ lines }: { readonly lines: readonly JevBudgetHistoryLine[] }): ReactNode {
  if (lines.length === 0) return null;
  return (
    <div className={styles.history}>
      <span className={styles.label}>History</span>
      <ul className={styles.log}>
        {lines.map((line) => (
          <li key={`${line.kind}:${line.at}`}>
            {formatDate(line.at)} · {line.byName ?? 'Someone'} {HISTORY_WORDS[line.kind]}
            {line.kind === 'asked' ? '' : ` ${jevBudgetLabel(line.microUsd)}`}
            {line.changedFromMicroUsd === null ? '' : ` (changed from ${jevBudgetLabel(line.changedFromMicroUsd)})`}
          </li>
        ))}
      </ul>
    </div>
  );
}
